import React, { useContext, useEffect } from 'react';
import { BulkImportTargetingView, BulkImportTargetingViewProps } from './BulkImportTargeting.view';
import { useWatch } from 'react-hook-form';
import { BulkActionsContext } from '../states/BulkActionsProvider';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { TikTokProfileValues } from '../../../components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { mapSocialProfilesWithBoardsToPostGroupProfiles } from '../../../helpers/mapSocialProfilesWithBoardsToPostGroupProfiles';
import { usePrevious } from '../../../hooks/usePrevious';

export type BulkImportTargetingControllerProps = {
  // extend your BulkImportTargetingViewProps
  formInstance: BulkImportTargetingViewProps['formInstance'];
};

export const BulkImportTargetingController = (props: BulkImportTargetingControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { formInstance } = props;
  const viewProps: BulkImportTargetingViewProps = { ...props };
  const { dispatch, profiles: socialProfiles } = useContext(BulkActionsContext);

  const watchCategories: string | undefined = useWatch({
    control: formInstance.control,
    name: `selectCategory`
  });

  const watchProfiles: Array<string | false> | undefined = useWatch({
    control: formInstance.control,
    name: `selectProfile`
  });

  const watchPnBoards: Array<string | undefined> | undefined = useWatch({
    control: formInstance.control,
    name: `selectProfile_pinterestBoard`
  });

  const watchTikToks: Array<TikTokProfileValues | undefined> | undefined = useWatch({
    control: formInstance.control,
    name: `selectProfile_tikTok`
  });

  const stringifiedProfiles = JSON.stringify(watchProfiles);
  const stringifiedPnBoards = JSON.stringify(watchPnBoards);
  const stringifiedTikTokSettings = JSON.stringify(watchTikToks);
  const previousStringifiedProfiles = usePrevious(stringifiedProfiles);

  // if some dependencies change we need to update profiles that we have in post editor state. the selected ones
  useEffect(() => {
    if (!stringifiedProfiles) return;
    const parsedProfiles: Array<string | false> = JSON.parse(stringifiedProfiles);
    const parsedBoards: Array<string> = stringifiedPnBoards ? JSON.parse(stringifiedPnBoards) : [];
    const tikTokValues: Array<TikTokProfileValues | null> = stringifiedTikTokSettings
      ? JSON.parse(stringifiedTikTokSettings)
      : null;
    // Only dispatch the update if the previous profiles aren't the same as the new ones. This is because the first
    // time the post editor loads, we set the form value for the profiles and don't want to update the state again
    // which would trigger validation.
    if (
      parsedProfiles.length &&
      previousStringifiedProfiles !== undefined &&
      previousStringifiedProfiles !== stringifiedProfiles
    ) {
      const profiles: SelectedSocialProfileValue[] = mapSocialProfilesWithBoardsToPostGroupProfiles(
        socialProfiles,
        parsedProfiles,
        parsedBoards,
        tikTokValues
      );
      dispatch({ type: 'updateProfiles', payload: profiles });
    }
  }, [
    socialProfiles,
    stringifiedProfiles,
    stringifiedPnBoards,
    stringifiedTikTokSettings,
    dispatch,
    previousStringifiedProfiles
  ]);

  useEffect(() => {
    if (watchCategories) {
      dispatch({ type: 'updateCategory', payload: watchCategories });
    }
  }, [watchCategories, dispatch]);

  return <BulkImportTargetingView {...viewProps} />;
};
